import type { components } from "@sagot/api-client";
import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, ErrorState, Icon, Skeleton } from "@sagot/ui";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { ServiceStatusChip } from "../../service/chips/service-chips";
import { statusLabelOf } from "../../service/organisations/ticket-parts";
import { CategorySection } from "./category-chip";
import { useConversationTickets, useTicketSupport } from "./use-conversation-tickets";

type LinkedTicket = components["schemas"]["TicketSummary"];

/** One ticket as a link chip: the ticket icon, its number and its status (the mockup's `ib-tkt-chip`). */
function TicketChip({ ticket, language }: { ticket: LinkedTicket; language: string }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  return (
    <AppLink
      href={consoleRoutes.tickets(summary.slug, ticket.number)}
      aria-label={t("service.inbox.chip.open", { number: ticket.number })}
      data-testid="thread-ticket-chip"
      className="inline-flex items-center gap-1.5 rounded-pill border border-line bg-surface py-0.5 pl-2 pr-1 text-xs hover:border-primary focus-visible:outline-2 focus-visible:outline-primary"
    >
      <Icon name="ticket" className="size-3.5 text-fg-muted" />
      <span className="font-mono font-bold text-primary-ink">{ticket.number}</span>
      <ServiceStatusChip category={ticket.status.category} label={statusLabelOf(ticket.status.label, language) ?? ticket.status.key} />
    </AppLink>
  );
}

/** The tickets of this chat in the thread header; nothing at all when the workspace has no Service Desk or the chat has no ticket. */
export function ThreadTicketChips({ conversationId }: { conversationId: string }) {
  const { i18n } = useTranslation();
  const support = useTicketSupport();
  const tickets = useConversationTickets(conversationId, support.canSee);
  const items = tickets.data?.items ?? [];
  if (items.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5" data-testid="thread-ticket-chips">
      {items.map((ticket) => (
        <TicketChip key={ticket.id} ticket={ticket} language={i18n.language} />
      ))}
    </span>
  );
}

/** Whether the chat already has a ticket (the header then shows its chip instead of "Convert to ticket"). */
export function useHasTicket(conversationId: string): boolean {
  const support = useTicketSupport();
  const tickets = useConversationTickets(conversationId, support.canSee);
  return (tickets.data?.items.length ?? 0) > 0;
}

/** "Linked tickets" in the Details pane: each ticket with its title, or the nudge to convert the chat. */
export function LinkedTickets({ conversationId }: { conversationId: string }) {
  const { t, i18n } = useTranslation();
  const { summary } = useWorkspaceContext();
  const support = useTicketSupport();
  const tickets = useConversationTickets(conversationId, support.canSee);
  if (!support.canSee) return null;
  const items = tickets.data?.items ?? [];
  return (
    <section data-testid="linked-tickets" aria-label={t("service.inbox.linked.title")}>
      <div className="label">{t("service.inbox.linked.title")}</div>
      {tickets.isPending ? (
        <Skeleton className="mt-1 h-14 w-full" />
      ) : tickets.isError ? (
        <ErrorState headingLevel={3} className="py-2" title={t("service.inbox.linked.error")} retryLabel={t("service.inbox.linked.retry")} onRetry={() => void tickets.refetch()} />
      ) : items.length === 0 ? (
        <p className="text-xs text-fg-subtle">{t("service.inbox.linked.none")}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((ticket) => (
            <li key={ticket.id}>
              <AppLink
                href={consoleRoutes.tickets(summary.slug, ticket.number)}
                aria-label={t("service.inbox.linked.open", { number: ticket.number })}
                className="block rounded-md border border-line px-2.5 py-2 hover:border-primary focus-visible:outline-2 focus-visible:outline-primary"
              >
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs font-bold text-primary-ink">{ticket.number}</span>
                  <ServiceStatusChip category={ticket.status.category} label={statusLabelOf(ticket.status.label, i18n.language) ?? ticket.status.key} />
                </span>
                <span className="mt-1 block text-xs text-fg-muted [overflow-wrap:anywhere]">{ticket.title}</span>
              </AppLink>
              <CategorySection ticket={ticket} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Used by the header: the convert button only when the member may and the chat has no ticket yet. */
export function ConvertButton({ canConvert, onClick }: { canConvert: boolean; onClick: () => void }) {
  const { t } = useTranslation();
  if (!canConvert) return null;
  return (
    <Button size="sm" onClick={onClick} data-testid="convert-to-ticket">
      <Icon name="ticket" />
      {t("service.inbox.convert.button")}
    </Button>
  );
}
